// SPDX-License-Identifier: AGPL-3.0-or-later

import type {HolidayBatFlockPlan, HolidayBatSprite, HolidayFlybyPath} from '@app/features/holiday/state/Holiday';
import {isHolidayCategoryEnabled} from '@app/features/holiday/state/HolidayPreferences';
import {cooldownElapsed} from '@app/features/holiday/utils/holidayCooldown';
import {holidayIntensity, holidaySeasonRamp} from '@app/features/holiday/utils/holidayIntensity';
import {isHolidaySeasonEnabled, resolveHolidaySeasonWindow} from '@app/features/holiday/utils/holidaySeason';

/** DEV-only art loop. `null` uses tryStartHolidayFlyby only. Production builds never force. */
export const DEBUG_FORCE_FLYING_BATS: true | null = null;

/**
 * Flock counts pin the ramp's value at peakStart; the ramp itself comes from holidayIntensity.
 *
 * A group caps at 20. Density past that comes from FLYBY_EXTRA_FLOCK_* — several mid-sized groups
 * crossing together rather than one huge one. These waypoints also make BAT_INTENSITY_OCT1 exactly
 * 1/6, which is where the shared intensity curve sits on Oct 1.
 *
 * Only the ceiling climbs: the roll always reaches down to BAT_COUNT_MIN, so a late-October night
 * mixes pairs, handfuls and the occasional full group rather than every group being the same size.
 */
export const BAT_COUNT_START = 2;
export const BAT_COUNT_OCT1 = 5;
export const BAT_COUNT_END_MAX = 20;
export const BAT_COUNT_MIN = BAT_COUNT_START;
export const BAT_COUNT_MAX = BAT_COUNT_END_MAX;
export const BAT_DEBUG_COUNT_MIN = 5;
export const BAT_DEBUG_COUNT_MAX = 20;
export const BAT_DISPLAY_FLOCK_MIN = 1;
export const BAT_DISPLAY_FLOCK_MAX = 3;
export const FLYBY_DISPLAY_GAP_MIN_MS = 2000;
export const FLYBY_DISPLAY_GAP_MAX_MS = 3500;
/** Intensity at local midnight Oct 1 so lerp(2, 20, t) is 5. */
export const BAT_INTENSITY_OCT1 = (BAT_COUNT_OCT1 - BAT_COUNT_START) / (BAT_COUNT_END_MAX - BAT_COUNT_START);
export const BAT_SIZE_MIN_REM = 0.85;
export const BAT_SIZE_MAX_REM = 2.5;
export const BAT_SCALE_NEAR = 1.42;
export const BAT_SCALE_FAR = 0.62;
export const BAT_BAND_MIN = 0.06;
export const BAT_BAND_MAX = 0.72;
export const BAT_BAND_DELTA_SOFT = 0.12;
export const BAT_BAND_DELTA_STEEP = 0.4;
export const BAT_FLOCK_OPACITY_NEAR = 1;
export const BAT_FLOCK_OPACITY_FAR = 0.78;
export const BAT_FLAP_DURATION_REF_MS = 420;
export const BAT_FLAP_DURATION_MIN_MS = 260;
export const BAT_FLAP_DURATION_MAX_MS = 900;
export const FLYBY_COOLDOWN_MAX_MS = 25_000;
export const FLYBY_COOLDOWN_MIN_MS = 4_500;
/**
 * Idle timer only. Shared event cooldown stays on FLYBY_COOLDOWN_*.
 *
 * October is the live half of this curve, so its waypoint is minutes rather than tens of minutes:
 * the peak phase lerps linearly from OCT1 to MIN, which puts Oct 25 at about a minute between
 * groups and Oct 15 at a little over two. Reaching a minute on the 25th is what sets OCT1 at 4min
 * — from 17min no floor above zero gets there by then. September rides the quadratic preseason
 * easing off GAP_MAX and stays quiet until the very end of the month.
 */
export const FLYBY_AMBIENT_GAP_MAX_MS = 33 * 60 * 1000;
export const FLYBY_AMBIENT_GAP_OCT1_MS = 4 * 60 * 1000;
export const FLYBY_AMBIENT_GAP_MIN_MS = 15 * 1000;
/**
 * The first roll after the scheduler arms. It used to wait a whole season gap, so opening the app
 * meant a minute of empty sky on the 25th and several minutes earlier in the month — and every
 * reload or tab switch started that wait over, which is exactly the window someone checking
 * whether bats work would sit through. Capped by the season gap so it is never the slower of the
 * two, and randomised so two tabs do not fire together.
 */
export const FLYBY_AMBIENT_FIRST_GAP_MIN_MS = 3_000;
export const FLYBY_AMBIENT_FIRST_GAP_MAX_MS = 12_000;
/**
 * Where density comes from now that a group caps at 20. Each extra is rolled against this and the
 * next is only tried if the last one landed, so the cap is a ceiling rather than a target and the
 * chance has to sit high to reach it: at peak the expected total is about 4.5 groups of 6.
 *
 * One wave has to fit inside MAX_CONCURRENT_BAT_FLOCKS, hence 5 extras and not more: past that the
 * store evicts the oldest flock in the store, which during a single wave is the primary one, and it
 * would vanish mid-crossing. Overlap between waves still evicts, which is what it is there for.
 *
 * The chance itself ramps linearly across October (see extraBatFlockChance), so Oct 1 still gets
 * single groups and the middle of the month is where the count starts to vary.
 */
export const FLYBY_EXTRA_FLOCK_CHANCE_MAX = 0.88;
export const FLYBY_EXTRA_FLOCK_MAX = 5;
export const FLYBY_EXTRA_STAGGER_MIN_MS = 200;
export const FLYBY_EXTRA_STAGGER_MAX_MS = 600;
/**
 * Any message posted in the channel you are looking at, whoever sent it. High on purpose: the
 * shared FLYBY_COOLDOWN_* is what actually paces this, so in a busy channel the cooldown is the
 * limit rather than the roll.
 */
export const FLYBY_SEND_CHANCE_MIN = 0.3;
export const FLYBY_SEND_CHANCE_MAX = 1;
export const FLYBY_CHANNEL_SWITCH_CHANCE_MIN = 0.36;
export const FLYBY_CHANNEL_SWITCH_CHANCE_MAX = 0.9;
/** Keep this fraction of a night flock during daylight (cut 70%, round down). */
export const FLYBY_DAYLIGHT_COUNT_KEEP = 0.3;
export const FLYBY_REACTION_GAP_MS = 200;
export const FLYBY_NIGHT_START_HOUR = 17;
export const FLYBY_NIGHT_END_HOUR = 7;
/**
 * Night is this much more common than daylight: the same season curve with the day gap stretched
 * by it. Only 1.5x, so day and night differ mostly in flock size rather than in how often a flock
 * comes — daylight still takes the 70% count cut, so a day group is a few bats against a night
 * group's dozens.
 */
export const FLYBY_DAYLIGHT_GAP_SCALE = 1.5;
/**
 * Halloween night finale. The gap collapses to just under one screen crossing, so the next flock
 * launches before the last has left and the sky is roughly 80% occupied. Ramped across Halloween
 * day rather than switched on, so the last afternoon builds into it instead of jumping at 5pm.
 */
export const FLYBY_FINALE_GAP_MS = 5500;
export const FLYBY_FINALE_RAMP_FROM_MS = 24 * 60 * 60 * 1000;
export const FLYBY_FINALE_RAMP_TO_MS = (24 - FLYBY_NIGHT_START_HOUR) * 60 * 60 * 1000;
const SPOOPY_WORD = /\bspoopy\b/i;
export const FLYBY_DURATION_MIN_MS = 3000;
export const FLYBY_DURATION_MAX_MS = 6000;
export const FLYBY_CROSS_WIDTH_RATIO = 1.4;
export const FLYBY_SPEED_PX_PER_MS = 0.4;
/**
 * Scaled with BAT_COUNT_* and, as before, running a touch above it: a rise is one brief burst out
 * of a reaction chip, not a group that keeps crossing. The helix rings on index/count, so a higher
 * count only thickens the column.
 */
export const BAT_RISE_COUNT_START_MIN = 5;
export const BAT_RISE_COUNT_START_MAX = 7;
export const BAT_RISE_COUNT_OCT1_MIN = 7;
export const BAT_RISE_COUNT_OCT1_MAX = 10;
export const BAT_RISE_COUNT_END_MIN = 20;
export const BAT_RISE_COUNT_END_MAX = 23;
export const BAT_RISE_COUNT_MIN = BAT_RISE_COUNT_START_MIN;
export const BAT_RISE_COUNT_MAX = BAT_RISE_COUNT_END_MAX;
export const BAT_RISE_DURATION_MIN_MS = 2200;
export const BAT_RISE_DURATION_MAX_MS = 3800;
export const BAT_RISE_SIZE_MIN_REM = 2.15;
export const BAT_RISE_SIZE_MAX_REM = 3.55;
export const BAT_RISE_SCALE_FROM = 0.78;
export const BAT_RISE_SCALE_TO = 1.12;
export const BAT_CROSS_WAVE_COUNT_MIN = 0.7;
export const BAT_CROSS_WAVE_COUNT_MAX = 1.15;
export const BAT_CROSS_WAVE_AMP_MIN = 0.0132;
export const BAT_CROSS_WAVE_AMP_MAX = 0.0275;
export const BAT_CROSS_WAVE_LAG_PER_REM = 0.28;
export const BAT_CROSS_SPREAD_X_REM = 4;
export const BAT_CROSS_SPREAD_Y_REM = 1.8;
/**
 * Per-index offset, which is a straight line before the random spread is added. At the original
 * 12-bat flocks the spread dominated it and a group read as a cluster; at 20 the line would start
 * to show, so these are scaled by sqrt(12/20) — footprint grows with the square root of the count,
 * holding density roughly constant and keeping the skein loose rather than ruler-straight.
 */
export const BAT_CROSS_INDEX_GAP_X_REM = 0.28;
export const BAT_CROSS_INDEX_GAP_Y_REM = 0.36;
export const BAT_FLAP_SIZE_REF_REM = (BAT_SIZE_MIN_REM + BAT_SIZE_MAX_REM) / 2;
export const BAT_FLAP_SIZE_JITTER = 0.1;
export const BAT_FLAP_FM_K = 0.4;
export const BAT_RISE_OPACITY_FROM = 1;
export const BAT_RISE_OPACITY_TO = 0;
export const BAT_RISE_Y_MIN = 0.36;
export const BAT_RISE_Y_MAX = 0.52;
export const BAT_RISE_X_SPREAD = 0.045;
export const BAT_RISE_HELIX_TURNS_MIN = 1.15;
export const BAT_RISE_HELIX_TURNS_MAX = 1.75;
export const BAT_RISE_HELIX_RADIUS_FROM_MIN_PX = 6;
export const BAT_RISE_HELIX_RADIUS_FROM_MAX_PX = 12;
export const BAT_RISE_HELIX_CONE_MIN = 0.3;
export const BAT_RISE_HELIX_CONE_MAX = 0.5;
export const BAT_RISE_HELIX_RADIUS_TO_MIN_PX = 96;
export const BAT_RISE_HELIX_RADIUS_TO_MAX_PX = 220;
export const BAT_RISE_HELIX_DEPTH = 0.5;
export const BAT_HELIX_SAMPLES = 48;

export interface PlanBatFlockOptions {
	path?: HolidayFlybyPath;
	origin?: {x: number; y: number};
	viewportHeightPx?: number;
	count?: number;
	countKeep?: number;
}

export interface HolidayFlybyGateInput {
	season: boolean;
	flybysEnabled: boolean;
	reducedMotion: boolean;
	documentVisible: boolean;
	lastSpawnAt: number | null;
	now: number;
	cooldownMs: number;
	night: boolean;
	forced?: boolean;
	bypassCooldown?: boolean;
	bypassNight?: boolean;
}

function mulberry32(seed: number): () => number {
	let a = Math.floor(seed * 0xffffffff) >>> 0;
	if (a === 0) a = 0x9e3779b9;
	return () => {
		a = (a + 0x6d2b79f5) >>> 0;
		let t = a;
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function lerp(from: number, to: number, t: number): number {
	return from + (to - from) * t;
}

function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}

export interface BatHelixInput {
	radiusFromPx: number;
	radiusToPx: number;
	riseYPx: number;
	driftXPx: number;
	turns: number;
	phaseRad: number;
	clockwise: boolean;
	samples?: number;
}

export function sampleBatHelix(input: BatHelixInput): Array<{x: number; y: number}> {
	const samples = input.samples ?? BAT_HELIX_SAMPLES;
	const sign = input.clockwise ? 1 : -1;
	const points: Array<{x: number; y: number}> = [];
	for (let index = 0; index <= samples; index++) {
		const t = index / samples;
		const theta = input.phaseRad + sign * t * input.turns * Math.PI * 2;
		const radius = lerp(input.radiusFromPx, input.radiusToPx, t * t);
		points.push({
			x: input.driftXPx * t + radius * Math.cos(theta),
			y: input.riseYPx * t + radius * Math.sin(theta) * BAT_RISE_HELIX_DEPTH,
		});
	}
	return points;
}

export function batHelixPath(points: ReadonlyArray<{x: number; y: number}>): string {
	return points
		.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
		.join(' ');
}

export function polylineLengthPx(points: ReadonlyArray<{x: number; y: number}>): number {
	let length = 0;
	for (let index = 1; index < points.length; index++) {
		const from = points[index - 1];
		const to = points[index];
		if (from == null || to == null) continue;
		length += Math.hypot(to.x - from.x, to.y - from.y);
	}
	return length;
}

export function buildBatHelix(input: BatHelixInput): {path: string; lengthPx: number} {
	const points = sampleBatHelix(input);
	return {path: batHelixPath(points), lengthPx: polylineLengthPx(points)};
}

export function halloweenFlybyIntensity(now: Date): number {
	return holidayIntensity('halloween', now, {peakAnchor: BAT_INTENSITY_OCT1});
}

/**
 * The season raises the ceiling, not the floor. A narrow band that slid upward meant every group on
 * a given night was within a bat or two of the same size; this rolls the whole way from a pair up to
 * whatever the season allows.
 */
export function batFlockCountRange(intensity: number): {min: number; max: number} {
	const t = clamp(intensity, 0, 1);
	const max = Math.round(lerp(BAT_COUNT_START, BAT_COUNT_END_MAX, t));
	return {min: BAT_COUNT_MIN, max: Math.max(BAT_COUNT_MIN, max)};
}

export function batFlockCount(intensity: number, rollUnit: number): number {
	const {min, max} = batFlockCountRange(intensity);
	const span = max - min;
	return min + Math.floor(clamp(rollUnit, 0, 0.999999) * (span + 1));
}

export function batRiseFlockCountRange(intensity: number): {min: number; max: number} {
	const t = clamp(intensity, 0, 1);
	if (t <= BAT_INTENSITY_OCT1) {
		const september = BAT_INTENSITY_OCT1 === 0 ? 1 : t / BAT_INTENSITY_OCT1;
		const min = Math.round(lerp(BAT_RISE_COUNT_START_MIN, BAT_RISE_COUNT_OCT1_MIN, september));
		const max = Math.round(lerp(BAT_RISE_COUNT_START_MAX, BAT_RISE_COUNT_OCT1_MAX, september));
		return {min, max: Math.max(min, max)};
	}
	const october = octoberFlybyProgress(t);
	const min = Math.round(lerp(BAT_RISE_COUNT_OCT1_MIN, BAT_RISE_COUNT_END_MIN, october));
	const max = Math.round(lerp(BAT_RISE_COUNT_OCT1_MAX, BAT_RISE_COUNT_END_MAX, october));
	return {min, max: Math.max(min, max)};
}

export function batRiseFlockCount(intensity: number, rollUnit: number): number {
	const {min, max} = batRiseFlockCountRange(intensity);
	const span = max - min;
	return min + Math.floor(clamp(rollUnit, 0, 0.999999) * (span + 1));
}

export function debugFlockCount(rollUnit: number): number {
	const span = BAT_DEBUG_COUNT_MAX - BAT_DEBUG_COUNT_MIN;
	return BAT_DEBUG_COUNT_MIN + Math.floor(clamp(rollUnit, 0, 0.999999) * (span + 1));
}

export function displayWaveFlockCount(rollUnit: number): number {
	const span = BAT_DISPLAY_FLOCK_MAX - BAT_DISPLAY_FLOCK_MIN;
	return BAT_DISPLAY_FLOCK_MIN + Math.floor(clamp(rollUnit, 0, 0.999999) * (span + 1));
}

export function displayLoopGapMs(rollUnit: number): number {
	return Math.round(lerp(FLYBY_DISPLAY_GAP_MIN_MS, FLYBY_DISPLAY_GAP_MAX_MS, clamp(rollUnit, 0, 1)));
}

export function displayLoopDelayMs(holdMs: number, rollUnit: number): number {
	return Math.max(Math.max(0, holdMs), displayLoopGapMs(rollUnit));
}

export function octoberFlybyProgress(intensity: number): number {
	if (intensity <= BAT_INTENSITY_OCT1) return 0;
	return clamp((intensity - BAT_INTENSITY_OCT1) / (1 - BAT_INTENSITY_OCT1), 0, 1);
}

/**
 * Linear across October rather than squared. Squared kept this near zero until the last few days,
 * so the whole middle of the month only ever got single groups; linear still starts at 0 on Oct 1
 * but reaches a coin flip by the 25th, which is what makes the group count vary.
 */
export function extraBatFlockChance(intensity: number): number {
	return octoberFlybyProgress(intensity) * FLYBY_EXTRA_FLOCK_CHANCE_MAX;
}

export function extraFlockStaggerMs(roll: number): number {
	return Math.round(lerp(FLYBY_EXTRA_STAGGER_MIN_MS, FLYBY_EXTRA_STAGGER_MAX_MS, clamp(roll, 0, 1)));
}

export function daylightKeptCount(count: number, keep: number = FLYBY_DAYLIGHT_COUNT_KEEP): number {
	return Math.floor(Math.max(0, count) * clamp(keep, 0, 1));
}

export function flybyCooldownMs(intensity: number): number {
	const t = clamp(intensity, 0, 1);
	return Math.round(lerp(FLYBY_COOLDOWN_MAX_MS, FLYBY_COOLDOWN_MIN_MS, t * t));
}

/**
 * 0 until a day is left in the peak window, then 0→1 across it, reaching 1 at nightfall so the
 * whole of Halloween night sits on the finale gap. Read off the resolved window rather than a
 * hardcoded Oct 31, and 0 in preseason so a short window cannot finale before its peak.
 */
export function finaleFlybyProgress(now: Date): number {
	const resolved = resolveHolidaySeasonWindow('halloween', now);
	if (resolved == null || resolved.phase !== 'peak') return 0;
	const remainingMs = resolved.seasonEnd.getTime() - now.getTime();
	const span = FLYBY_FINALE_RAMP_FROM_MS - FLYBY_FINALE_RAMP_TO_MS;
	if (span <= 0) return remainingMs <= FLYBY_FINALE_RAMP_TO_MS ? 1 : 0;
	return clamp((FLYBY_FINALE_RAMP_FROM_MS - remainingMs) / span, 0, 1);
}

/**
 * Idle gap, on the shared season ramp but its own easing: quadratic through preseason, linear
 * through peak, then collapsing to FLYBY_FINALE_GAP_MS across the last day. Daylight keeps the same
 * curve stretched by FLYBY_DAYLIGHT_GAP_SCALE. Out of season returns the widest gap; the ambient
 * scheduler is season-gated anyway.
 */
export function ambientFlybyGapMs(now: Date): number {
	const ramp = holidaySeasonRamp('halloween', now);
	if (ramp == null) return FLYBY_AMBIENT_GAP_MAX_MS;
	const seasonGapMs =
		ramp.phase === 'preseason'
			? lerp(FLYBY_AMBIENT_GAP_MAX_MS, FLYBY_AMBIENT_GAP_OCT1_MS, ramp.progress * ramp.progress)
			: lerp(
					lerp(FLYBY_AMBIENT_GAP_OCT1_MS, FLYBY_AMBIENT_GAP_MIN_MS, ramp.progress),
					FLYBY_FINALE_GAP_MS,
					finaleFlybyProgress(now),
				);
	return Math.round(seasonGapMs * (isHalloweenFlybyNight(now) ? 1 : FLYBY_DAYLIGHT_GAP_SCALE));
}

/** Delay for the first ambient roll after the scheduler arms. Never slower than the steady gap. */
export function firstAmbientFlybyGapMs(now: Date, roll: number): number {
	const first = Math.round(lerp(FLYBY_AMBIENT_FIRST_GAP_MIN_MS, FLYBY_AMBIENT_FIRST_GAP_MAX_MS, clamp(roll, 0, 1)));
	return Math.min(ambientFlybyGapMs(now), first);
}

export function sendFlybyChance(intensity: number): number {
	return lerp(FLYBY_SEND_CHANCE_MIN, FLYBY_SEND_CHANCE_MAX, clamp(intensity, 0, 1));
}

export function isHalloweenFlybyNight(now: Date): boolean {
	const hour = now.getHours();
	return hour >= FLYBY_NIGHT_START_HOUR || hour < FLYBY_NIGHT_END_HOUR;
}

export function containsSpoopyWord(content: string | null | undefined): boolean {
	if (content == null || content === '') return false;
	return SPOOPY_WORD.test(content);
}

/**
 * Any message in the channel on screen answers, not just your own — a flyby is something the room
 * sees together, so it should not depend on who typed. Spoopy still answers from anywhere, since
 * it is a word someone chose; an ordinary message elsewhere is not worth a flyby over here.
 */
export function postedMessageFlybyReason(content: string, inCurrentChannel: boolean): 'spoopy' | 'send' | null {
	if (containsSpoopyWord(content)) return 'spoopy';
	if (inCurrentChannel) return 'send';
	return null;
}

export function shouldStartSendFlyby(roll: number, intensity: number): boolean {
	return roll < sendFlybyChance(intensity);
}

export function channelSwitchFlybyChance(intensity: number): number {
	if (intensity < BAT_INTENSITY_OCT1) return 0;
	const october = octoberFlybyProgress(intensity);
	return lerp(FLYBY_CHANNEL_SWITCH_CHANCE_MIN, FLYBY_CHANNEL_SWITCH_CHANCE_MAX, october * october);
}

export function shouldStartChannelSwitchFlyby(now: Date, roll: number): boolean {
	// No month guard: channelSwitchFlybyChance is already 0 below the Oct 1 waypoint, and
	// halloweenFlybyIntensity is 0 out of season.
	return roll < channelSwitchFlybyChance(halloweenFlybyIntensity(now));
}

export function batFlapDurationForTravelPx(durationMs: number, travelPx: number): number {
	const speedPxPerMs = Math.max(1, travelPx) / Math.max(1, durationMs);
	const duration = BAT_FLAP_DURATION_REF_MS * (FLYBY_SPEED_PX_PER_MS / Math.max(0.001, speedPxPerMs));
	return Math.round(Math.min(BAT_FLAP_DURATION_MAX_MS, Math.max(BAT_FLAP_DURATION_MIN_MS, duration)));
}

export function batFlapDurationMs(durationMs: number, viewportWidthPx: number): number {
	return batFlapDurationForTravelPx(durationMs, Math.max(1, viewportWidthPx) * FLYBY_CROSS_WIDTH_RATIO);
}

export function batFlapDurationForSize(refDurationMs: number, sizeRem: number, jitterRoll: number): number {
	const sizeScale = Math.sqrt(Math.max(0.001, sizeRem) / BAT_FLAP_SIZE_REF_REM);
	const jitter = lerp(1 - BAT_FLAP_SIZE_JITTER, 1 + BAT_FLAP_SIZE_JITTER, clamp(jitterRoll, 0, 1));
	return Math.round(clamp(refDurationMs * sizeScale * jitter, BAT_FLAP_DURATION_MIN_MS, BAT_FLAP_DURATION_MAX_MS));
}

export function crossFacing(direction: HolidayBatFlockPlan['direction']): 1 | -1 {
	return direction === 'rtl' ? -1 : 1;
}

export function crossTiltRef(drift: number, waveAmpFrac: number, waveCount: number): number {
	const wavePeak = Math.max(0, waveAmpFrac) * Math.PI * 2 * Math.max(0, waveCount);
	return Math.max(0.02, Math.abs(drift) + wavePeak);
}

export function batFlapFm(cycles: number, waveCount: number, k: number = BAT_FLAP_FM_K): number {
	return (Math.max(0, cycles) * clamp(k, 0, 1)) / (Math.PI * 2 * Math.max(0.001, waveCount));
}

function planCrossFlock(
	next: () => number,
	viewportWidthPx: number,
	intensity: number,
	countOverride?: number,
	countKeep = 1,
): HolidayBatFlockPlan {
	const rawCount = countOverride ?? batFlockCount(intensity, next());
	const count = daylightKeptCount(rawCount, countKeep);
	const direction = next() < 0.5 ? 'ltr' : 'rtl';
	const approaching = next() < 0.5;
	const scaleFrom = approaching ? BAT_SCALE_FAR : BAT_SCALE_NEAR;
	const scaleTo = approaching ? BAT_SCALE_NEAR : BAT_SCALE_FAR;
	const opacityFrom = approaching ? BAT_FLOCK_OPACITY_FAR : BAT_FLOCK_OPACITY_NEAR;
	const opacityTo = approaching ? BAT_FLOCK_OPACITY_NEAR : BAT_FLOCK_OPACITY_FAR;
	const bandFrom = lerp(BAT_BAND_MIN, BAT_BAND_MAX, next());
	const steep = next() < 0.4;
	const maxDelta = steep ? BAT_BAND_DELTA_STEEP : BAT_BAND_DELTA_SOFT;
	const bandTo = Math.min(BAT_BAND_MAX, Math.max(BAT_BAND_MIN, bandFrom + lerp(-maxDelta, maxDelta, next())));
	const widthMs = (Math.max(1, viewportWidthPx) * FLYBY_CROSS_WIDTH_RATIO) / FLYBY_SPEED_PX_PER_MS;
	const durationMs = Math.round(
		Math.min(FLYBY_DURATION_MAX_MS, Math.max(FLYBY_DURATION_MIN_MS, widthMs * lerp(0.92, 1.08, next()))),
	);
	const flapDurationMs = batFlapDurationMs(durationMs, viewportWidthPx);
	const waveCount = lerp(BAT_CROSS_WAVE_COUNT_MIN, BAT_CROSS_WAVE_COUNT_MAX, next());
	const waveAmpFrac = lerp(BAT_CROSS_WAVE_AMP_MIN, BAT_CROSS_WAVE_AMP_MAX, next());
	const wavePhase = next();
	const tiltRef = crossTiltRef(bandTo - bandFrom, waveAmpFrac, waveCount);
	const bats: Array<HolidayBatSprite> = [];
	const mid = (count - 1) / 2;
	for (let index = 0; index < count; index++) {
		const depth = next();
		const sizeRem = lerp(BAT_SIZE_MIN_REM, BAT_SIZE_MAX_REM, depth);
		const batFlapMs = batFlapDurationForSize(flapDurationMs, sizeRem, next());
		const flapDelayMs = Math.round(next() * batFlapMs);
		const offsetXRem =
			lerp(-BAT_CROSS_SPREAD_X_REM, BAT_CROSS_SPREAD_X_REM, next()) + (index - mid) * BAT_CROSS_INDEX_GAP_X_REM;
		const cycles = durationMs / Math.max(1, batFlapMs);
		bats.push({
			sizeRem,
			offsetXRem,
			offsetYRem:
				lerp(-BAT_CROSS_SPREAD_Y_REM, BAT_CROSS_SPREAD_Y_REM, next()) + (index - mid) * BAT_CROSS_INDEX_GAP_Y_REM,
			flapDelayMs,
			bobDelayMs: flapDelayMs,
			opacity: lerp(0.82, 1, depth),
			flapDurationMs: batFlapMs,
			waveLag: -offsetXRem * BAT_CROSS_WAVE_LAG_PER_REM,
			flapCycles: cycles,
			flapFm: batFlapFm(cycles, waveCount),
			flapOffset: flapDelayMs / batFlapMs,
		});
	}
	return {
		bats,
		direction,
		path: 'cross',
		bandFrom,
		bandTo,
		durationMs,
		scaleFrom,
		scaleTo,
		opacityFrom,
		opacityTo,
		flapDurationMs,
		waveCount,
		waveAmpFrac,
		wavePhase,
		tiltRef,
	};
}

function planRiseFlock(
	next: () => number,
	viewportWidthPx: number,
	viewportHeightPx: number,
	intensity: number,
	origin: {x: number; y: number},
	countKeep = 1,
): HolidayBatFlockPlan {
	const nightCount = batRiseFlockCount(intensity, next());
	const count = daylightKeptCount(nightCount, countKeep);
	const direction = next() < 0.5 ? 'ltr' : 'rtl';
	const clockwise = direction === 'ltr';
	const riseYPx = -lerp(BAT_RISE_Y_MIN, BAT_RISE_Y_MAX, next()) * Math.max(1, viewportHeightPx);
	const driftXPx = lerp(-BAT_RISE_X_SPREAD, BAT_RISE_X_SPREAD, next()) * Math.max(1, viewportWidthPx);
	const turns = lerp(BAT_RISE_HELIX_TURNS_MIN, BAT_RISE_HELIX_TURNS_MAX, next());
	const bats: Array<HolidayBatSprite> = [];
	let travelPx = Math.abs(riseYPx);
	for (let index = 0; index < count; index++) {
		const depth = next();
		const phaseRad = (index / count) * Math.PI * 2 + lerp(-0.35, 0.35, next());
		const helix = buildBatHelix({
			radiusFromPx: lerp(BAT_RISE_HELIX_RADIUS_FROM_MIN_PX, BAT_RISE_HELIX_RADIUS_FROM_MAX_PX, next()),
			radiusToPx: Math.min(
				BAT_RISE_HELIX_RADIUS_TO_MAX_PX,
				Math.max(
					BAT_RISE_HELIX_RADIUS_TO_MIN_PX,
					Math.abs(riseYPx) * lerp(BAT_RISE_HELIX_CONE_MIN, BAT_RISE_HELIX_CONE_MAX, depth),
				),
			),
			riseYPx,
			driftXPx,
			turns: turns * lerp(0.92, 1.08, next()),
			phaseRad,
			clockwise,
		});
		travelPx = Math.max(travelPx, helix.lengthPx);
		bats.push({
			sizeRem: lerp(BAT_RISE_SIZE_MIN_REM, BAT_RISE_SIZE_MAX_REM, depth),
			offsetXRem: 0,
			offsetYRem: 0,
			flapDelayMs: 0,
			bobDelayMs: 0,
			opacity: lerp(0.9, 1, depth),
			helixPath: helix.path,
		});
	}
	const durationMs = Math.round(
		Math.min(
			BAT_RISE_DURATION_MAX_MS,
			Math.max(BAT_RISE_DURATION_MIN_MS, (travelPx / FLYBY_SPEED_PX_PER_MS) * lerp(0.94, 1.06, next())),
		),
	);
	const flapDurationMs = batFlapDurationForTravelPx(durationMs, travelPx);
	for (const bat of bats) {
		bat.flapDelayMs = Math.round(next() * flapDurationMs);
	}
	return {
		bats,
		direction,
		path: 'rise',
		bandFrom: 0,
		bandTo: 0,
		durationMs,
		scaleFrom: BAT_RISE_SCALE_FROM,
		scaleTo: BAT_RISE_SCALE_TO,
		opacityFrom: BAT_RISE_OPACITY_FROM,
		opacityTo: BAT_RISE_OPACITY_TO,
		flapDurationMs,
		originX: origin.x,
		originY: origin.y,
		riseXPx: driftXPx,
		riseYPx,
	};
}

export function planBatFlock(
	roll: number,
	viewportWidthPx: number,
	intensity: number,
	options?: PlanBatFlockOptions,
): HolidayBatFlockPlan {
	const next = mulberry32(roll);
	if (options?.path === 'rise' && options.origin != null) {
		return planRiseFlock(
			next,
			viewportWidthPx,
			options.viewportHeightPx ?? 800,
			intensity,
			options.origin,
			options.countKeep,
		);
	}
	return planCrossFlock(next, viewportWidthPx, intensity, options?.count, options?.countKeep);
}

export function canStartHolidayFlyby(input: HolidayFlybyGateInput): boolean {
	if (input.reducedMotion) return false;
	if (!input.documentVisible) return false;
	if (input.forced) return true;
	if (!input.night && !input.bypassNight) return false;
	if (!input.season || !input.flybysEnabled) return false;
	if (!input.bypassCooldown && !cooldownElapsed(input.lastSpawnAt, input.now, input.cooldownMs)) {
		return false;
	}
	return true;
}

export function isHalloweenFlyingBatsForced(): boolean {
	return import.meta.env.DEV && DEBUG_FORCE_FLYING_BATS === true;
}

export function isHalloweenFlyingBatsEnabled(): boolean {
	if (isHalloweenFlyingBatsForced()) return true;
	if (!isHolidaySeasonEnabled('halloween')) return false;
	return isHolidayCategoryEnabled('flybys', 'halloween');
}

/** Ambient chat-background bats, distinct from event flybys. */
export function isHalloweenAmbientBatsEnabled(): boolean {
	if (isHalloweenFlyingBatsForced()) return true;
	if (!isHolidaySeasonEnabled('halloween')) return false;
	return isHolidayCategoryEnabled('backgroundEffects', 'halloween');
}
